/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef, useCallback } from "react";
import { ExecutionNodeLog } from "../services/agentExecutionsService";
import { API_CONFIG } from "@/infrastructure/config/api";
import {
  addAuthTokenToUrl,
  redactTokenInUrl,
} from "@/shared/utils/sseConnection";
import { logger } from "@/infrastructure/lib/logger";

/**
 * Interface para eventos de logs dos nós
 */
export interface NodeLogEvent {
  type: string;
  executionId?: string;
  nodeId?: string;
  status?: string;
  timestamp?: string;
  error_message?: string | null;
  // Propriedades obrigatórias do log
  id: string;
  execution_id: string;
  node_id: string;
  node_type: string;
  input?: any;
  output?: any;
  started_at: string;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
  node_schema?: any;
  [key: string]: any; // Para propriedades dinâmicas
}

/**
 * Interface para callbacks de eventos de logs dos nós
 */
export interface NodeLogCallbacks {
  onConnectionEstablished?: () => void;
  onExistingLogs?: (logs: ExecutionNodeLog[]) => void;
  onNodeStarted?: (data: NodeLogEvent) => void;
  onNodeCompleted?: (data: NodeLogEvent) => void;
  onNodeFailed?: (data: NodeLogEvent) => void;
  onNodeUpdated?: (data: NodeLogEvent) => void;
  onPing?: () => void;
  onError?: (event: Event) => void;
  onMessage?: (data: any) => void; // Para qualquer outro tipo de evento
}

interface UseNodeLogsSSEOptions {
  executionId: string;
  enabled?: boolean;
}

interface UseNodeLogsSSEResult {
  logs: ExecutionNodeLog[];
  isConnected: boolean;
  error: Error | null;
  reconnect: () => void;
  disconnect: () => void;
}

/**
 * Hook para gerenciar conexões SSE para logs dos nós de execução
 */
export function useNodeLogsSSE({
  executionId,
  enabled = true,
}: UseNodeLogsSSEOptions): UseNodeLogsSSEResult {
  const [logs, setLogs] = useState<ExecutionNodeLog[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Referência para o EventSource
  const eventSourceRef = useRef<EventSource | null>(null);

  // Função para ordenar logs por started_at e depois por status
  const sortLogs = useCallback((logs: ExecutionNodeLog[]) => {
    return logs.sort((a, b) => {
      // 1. Primeiro tenta ordenar por started_at (mais confiável)
      if (a.started_at && b.started_at && a.started_at !== b.started_at) {
        const timeA = new Date(a.started_at).getTime();
        const timeB = new Date(b.started_at).getTime();
        return timeA - timeB;
      }

      // 2. Se started_at for igual ou ausente, usa created_at
      if (a.created_at && b.created_at && a.created_at !== b.created_at) {
        const createdA = new Date(a.created_at).getTime();
        const createdB = new Date(b.created_at).getTime();
        return createdA - createdB;
      }

      // 3. Se uma entrada tem timestamp e outra não, prioriza a que tem
      const aHasTime = a.started_at || a.created_at;
      const bHasTime = b.started_at || b.created_at;
      if (aHasTime && !bHasTime) return -1;
      if (!aHasTime && bHasTime) return 1;

      // 4. Ordenação por tipo de nó (seguindo fluxo lógico)
      const nodeTypeOrder = {
        start: 0,
        input: 1,
        rag_assistant: 2,
        rest: 3,
        end: 4,
      };
      const typeA =
        nodeTypeOrder[a.node_type as keyof typeof nodeTypeOrder] ?? 999;
      const typeB =
        nodeTypeOrder[b.node_type as keyof typeof nodeTypeOrder] ?? 999;

      if (typeA !== typeB) {
        return typeA - typeB;
      }

      // 5. Por último, ordena por status
      const statusOrder = { starting: 0, running: 1, completed: 2, failed: 3 };
      const statusA = statusOrder[a.status as keyof typeof statusOrder] ?? 999;
      const statusB = statusOrder[b.status as keyof typeof statusOrder] ?? 999;

      return statusA - statusB;
    });
  }, []);

  // Função para atualizar ou adicionar um log
  const updateOrAddLog = useCallback(
    (newLog: ExecutionNodeLog) => {
      setLogs((prevLogs) => {
        // Busca por node_id ao invés de id para correlacionar corretamente
        const existingIndex = prevLogs.findIndex(
          (log) => log.node_id === newLog.node_id,
        );

        if (existingIndex !== -1) {
          // Atualiza log existente preservando campos não null
          const existingLog = prevLogs[existingIndex];
          const updatedLog = {
            ...existingLog,
            ...newLog,
            // Preserva o ID original do banco de dados
            id: existingLog.id,
            // Preserva started_at se o novo valor for vazio e existir valor anterior
            started_at: newLog.started_at || existingLog.started_at,
            // Atualiza completed_at apenas se vier preenchido
            completed_at: newLog.completed_at || existingLog.completed_at,
            // Sempre atualiza updated_at
            updated_at: new Date().toISOString(),
          };

          const newLogs = [...prevLogs];
          newLogs[existingIndex] = updatedLog;

          return sortLogs(newLogs);
        } else {
          // Adiciona novo log (só se não encontrar por node_id)
          const newLogs = [...prevLogs, newLog];

          return sortLogs(newLogs);
        }
      });
    },
    [sortLogs],
  );

  // Função para conectar ao SSE
  const connect = useCallback(() => {
    if (!executionId || !enabled) return;

    try {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Constrói a URL do SSE — gêmea v2: /executions/:id/node-logs/stream
      // (silentAuth aceita o mesmo ?token= que o addAuthTokenToUrl appenda)
      const baseUrl = `${API_CONFIG.API_ROOT_URL}/executions/${executionId}/node-logs/stream`;
      const urlWithTimestamp = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}_t=${Date.now()}`;

      const urlWithAuth = addAuthTokenToUrl(urlWithTimestamp);

      // Cria nova conexão EventSource
      const eventSource = new EventSource(urlWithAuth);
      eventSourceRef.current = eventSource;

      // Configurar callbacks
      const callbacks: NodeLogCallbacks = {
        onConnectionEstablished: () => {
          setIsConnected(true);
          setError(null);
        },

        onExistingLogs: (existingLogs) => {
          setLogs(sortLogs(existingLogs));
        },

        onNodeStarted: (data) => {
          if (data.nodeId && data.executionId) {
            // Atualiza ou cria log para o nó
            const logData = {
              id: data.nodeId, // Usa nodeId como identificador único do nó
              node_id: data.nodeId,
              execution_id: data.executionId,
              node_type: data.nodeType,
              status: "starting", // Força o status para starting
              started_at: new Date().toISOString(), // Timestamp atual para started
              completed_at: null, // Ainda não completado
              input: data.input || null,
              output: null, // Não há output ainda
              error_message: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              node_schema: null,
            } as ExecutionNodeLog;
            updateOrAddLog(logData);
          }
        },

        onNodeCompleted: (data) => {
          if (data.nodeId && data.executionId) {
            // Atualiza o log existente com status completed
            const logData = {
              id: data.nodeId, // Mesmo ID para atualizar o log existente
              node_id: data.nodeId,
              execution_id: data.executionId,
              node_type: data.nodeType,
              status: "completed", // Atualiza para completed
              started_at: "", // Preserva o valor existente (será mantido pelo merge)
              completed_at: new Date().toISOString(), // Timestamp atual para completed
              input: data.input || null,
              output: data.output || null,
              error_message: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              node_schema: null,
            } as ExecutionNodeLog;
            updateOrAddLog(logData);
          }
        },

        onNodeFailed: (data) => {
          if (data.nodeId && data.executionId) {
            // Atualiza o log existente com status failed
            const logData = {
              id: data.nodeId, // Mesmo ID para atualizar o log existente
              node_id: data.nodeId,
              execution_id: data.executionId,
              node_type: data.nodeType,
              status: "failed", // Atualiza para failed
              started_at: "", // Preserva o valor existente
              completed_at: new Date().toISOString(), // Timestamp do erro
              input: data.input || null,
              output: data.output || null,
              error_message:
                data.error_message || "Nó falhou durante a execução",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              node_schema: null,
            } as ExecutionNodeLog;
            updateOrAddLog(logData);
          }
        },

        onNodeUpdated: (data) => {
          if (data.nodeId && data.executionId) {
            // Atualiza o log existente preservando dados anteriores
            const logData = {
              id: data.nodeId, // Mesmo ID para atualizar o log existente
              node_id: data.nodeId,
              execution_id: data.executionId,
              node_type: data.nodeType,
              status: data.status || "running", // Mantém status atual ou define como running
              started_at: "", // Preserva valor existente
              completed_at:
                data.status === "completed" ? new Date().toISOString() : null,
              input: data.input || null,
              output: data.output || null,
              error_message: data.error_message || null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              node_schema: null,
            } as ExecutionNodeLog;
            updateOrAddLog(logData);
          }
        },


        onError: () => {
          // Sem o evento cru: ele expõe `target.url`, e a url do SSE carrega o
          // access token como query param (EventSource não aceita header).
          console.error("[useNodeLogsSSE] Erro na conexão SSE", {
            url: redactTokenInUrl(urlWithAuth),
            readyState: eventSourceRef.current?.readyState,
          });
          setError(new Error("Erro na conexão com o servidor"));
          setIsConnected(false);
        },

      };

      // Event listeners para o EventSource
      eventSource.addEventListener("connection-established", () => {
        callbacks.onConnectionEstablished?.();
      });

      eventSource.addEventListener("existing-logs", (event) => {
        try {
          const data = JSON.parse(event.data);
          callbacks.onExistingLogs?.(data.logs || []);
        } catch (err) {
          console.error(
            "[useNodeLogsSSE] Erro ao processar existing-logs:",
            err,
          );
        }
      });

      eventSource.addEventListener("node-started", (event) => {
        try {
          const data = JSON.parse(event.data);
          callbacks.onNodeStarted?.(data);
        } catch (err) {
          console.error(
            "[useNodeLogsSSE] Erro ao processar node-started:",
            err,
          );
        }
      });

      eventSource.addEventListener("node-completed", (event) => {
        try {
          const data = JSON.parse(event.data);
          callbacks.onNodeCompleted?.(data);
        } catch (err) {
          console.error(
            "[useNodeLogsSSE] Erro ao processar node-completed:",
            err,
          );
        }
      });

      eventSource.addEventListener("node-failed", (event) => {
        try {
          const data = JSON.parse(event.data);
          callbacks.onNodeFailed?.(data);
        } catch (err) {
          console.error("[useNodeLogsSSE] Erro ao processar node-failed:", err);
        }
      });

      eventSource.addEventListener("node-updated", (event) => {
        try {
          const data = JSON.parse(event.data);
          callbacks.onNodeUpdated?.(data);
        } catch (err) {
          console.error(
            "[useNodeLogsSSE] Erro ao processar node-updated:",
            err,
          );
        }
      });

      // The DB-poll fallback (new engine) streams full node-log rows as
      // `log-added`. Correlate by node_id (updateOrAddLog) so each node appears
      // while it is running and updates to its terminal status without a refresh.
      eventSource.addEventListener("log-added", (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.log?.node_id) {
            updateOrAddLog(data.log as ExecutionNodeLog);
          }
        } catch (err) {
          logger.error("Failed to process node log-added SSE event", {
            executionId,
            error:
              err instanceof Error
                ? { message: err.message, stack: err.stack, name: err.name }
                : { message: String(err) },
          });
        }
      });

      eventSource.addEventListener("ping", () => {
        callbacks.onPing?.();
      });

      eventSource.addEventListener("error", (event) => {
        callbacks.onError?.(event);
      });

      // Handler genérico para eventos não mapeados
      eventSource.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(event.data);
          callbacks.onMessage?.(data);
        } catch (err) {
          console.error(
            "[useNodeLogsSSE] Erro ao processar mensagem genérica:",
            err,
          );
        }
      });
    } catch (err) {
      console.error("[useNodeLogsSSE] Erro ao estabelecer conexão:", err);
      setError(
        err instanceof Error ? err : new Error("Erro ao conectar ao servidor"),
      );
      setIsConnected(false);
    }
  }, [executionId, enabled, updateOrAddLog, sortLogs]);

  // Efeito para gerenciar a conexão
  useEffect(() => {
    if (!executionId || !enabled) {
      return;
    }

    // Inicia a conexão
    connect();

    // Monitora visibilidade da página para reconectar quando o usuário volta
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        eventSourceRef.current?.readyState === EventSource.CLOSED
      ) {
        connect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup: fecha a conexão e remove event listeners
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  // Função para reconectar manualmente
  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  // Função para desconectar manualmente
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Debug log final

  return {
    logs,
    isConnected,
    error,
    reconnect,
    disconnect,
  };
}
